'use client';

import { useEffect } from 'react';
import { ALL_BOARD_ICONS } from '../features/game/board-data';
import {
  smallConceptIconUrl,
  versionedConceptAssetUrl,
} from '../features/game/asset-url';
import { BOARD_IMAGE_PREFETCH_RULES } from '../features/game/image-prefetch-rules';
import { useReducedImageData } from './use-reduced-image-data';

const PIECE_COLORS = ['green', 'blue', 'red', 'yellow', 'black'] as const;
const PIECE_KINDS = ['marker', 'cube'] as const;

export function boardImagePrefetchUrls(): string[] {
  return [
    ...ALL_BOARD_ICONS.map((icon) => smallConceptIconUrl(icon.src)),
    ...PIECE_KINDS.flatMap((kind) => PIECE_COLORS.map((color) => (
      versionedConceptAssetUrl(`/concept-pieces/${kind}-${color}.png`)
    ))),
  ];
}

export function useBoardImagePrefetch(active: boolean): void {
  const imageDataPreference = useReducedImageData();

  useEffect(() => {
    if (
      !active
      || !BOARD_IMAGE_PREFETCH_RULES.enabled
      || !imageDataPreference.known
      || (imageDataPreference.reduced && !BOARD_IMAGE_PREFETCH_RULES.prefetchOnReducedData)
    ) return;

    const urls = boardImagePrefetchUrls();
    const activeImages = new Set<HTMLImageElement>();
    const configuredConcurrency = imageDataPreference.reduced
      ? BOARD_IMAGE_PREFETCH_RULES.reducedDataConcurrency
      : BOARD_IMAGE_PREFETCH_RULES.concurrency;
    const concurrency = Math.max(1, Math.floor(configuredConcurrency));
    let cancelled = false;
    let nextIndex = 0;
    let timeoutId: number | null = null;
    let idleCallbackId: number | null = null;

    const pumpQueue = () => {
      if (cancelled || document.hidden) return;
      while (activeImages.size < concurrency && nextIndex < urls.length) {
        const image = new Image();
        const url = urls[nextIndex++];
        activeImages.add(image);
        image.decoding = 'async';
        image.fetchPriority = 'low';

        const complete = () => {
          image.onload = null;
          image.onerror = null;
          activeImages.delete(image);
          pumpQueue();
        };
        image.onload = complete;
        image.onerror = complete;
        image.src = url!;
      }
    };

    const startWhenIdle = () => {
      if (cancelled) return;
      if (typeof window.requestIdleCallback === 'function') {
        idleCallbackId = window.requestIdleCallback(pumpQueue, { timeout: 2000 });
      } else {
        pumpQueue();
      }
    };

    const resumeWhenVisible = () => {
      if (!document.hidden) pumpQueue();
    };

    document.addEventListener('visibilitychange', resumeWhenVisible);
    timeoutId = window.setTimeout(startWhenIdle, BOARD_IMAGE_PREFETCH_RULES.startDelayMs);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', resumeWhenVisible);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      if (idleCallbackId !== null) window.cancelIdleCallback(idleCallbackId);
      activeImages.forEach((image) => {
        image.onload = null;
        image.onerror = null;
      });
      activeImages.clear();
    };
  }, [active, imageDataPreference.known, imageDataPreference.reduced]);
}
